<template>
  <q-card class="inhdialog-container-narrow">
    <div class="inhdialog-header">
      <h1 class="dialog_name">{{ $t('warehouse_information') }}</h1>
      <app-icon
        name="close"
        width="18px"
        color="#979797"
        hover-color="#499259"
        cursor="pointer"
        :title="$t('close_label')"
        @click="close"
      />
    </div>

    <div class="dialog_content" style="overflow: hidden">

      <div class="field-group q-mt-sm">
        <div class="row no-wrap" style="align-items: baseline">
          <native-input
            ref="storage_name_input"
            :allow-line-break="false"
            :model-value="storageNameDraft"
            @update:modelValue="onStorageNameInput"
          />
          <app-icon
            v-if="currentStorage && CanWrite"
            name="edit-pencil"
            width="18px"
            color="#979797"
            hover-color="#499259"
            cursor="pointer"
            class="q-ml-xs"
            style="margin-top: 2px"
            @click.stop="focusInput"
          />
        </div>
      </div>

      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap justify-left">
          <div class="bold-name mr-xs">
            {{ $t('responsible_worker') }}:
          </div>
          <span>{{ selectedExistingResponsible ? selectedExistingResponsible.Name : currentStorage?.ResponsibleWorker?.Name }}</span>
          <q-btn
            ref="respMenuTrigger"
            :disable="!CanWrite"
            no-caps
            :label="$t('change_responsible')"
            flat
            @click.stop="handleMenuShow"
          />
          <selector-popup
            v-if="CanWrite"
            v-model:visible="showSelector"
            :trigger-el="$refs.respMenuTrigger"
            wide-layout
          >
            <aux-selector
              ref="aux-selector"
              v-model="tmp_resp"
              :model-type="3"
              :limit-height-to-container="true"
              :hide-checkboxes="true"
              :allow-multiple="true"
              @selectorClosed="showSelector = false"
            />
          </selector-popup>
        </div>
        <!-- <div class="field-group__row no-wrap justify-left">
          <div class="bold-name" v-if="selectedExistingResponsible">{{ $t('new_responsible') }}:</div>
          <div>
            {{ selectedExistingResponsible?.Name }}
          </div>
        </div> -->
      </div>

      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap justify-left">
          <div class="bold-name mr-xs">
            {{ $t('warehouse_group') }}:
          </div>
          <q-select
            :disable="!CanWrite"
            flat
            :model-value="currentStorageGroupId"
            @update:model-value="(arg) => {
              changeStorageGroup(arg);
              currentStorageGroupId = arg;
            }"
            style="min-width: 150px"
            class="thin-input q-mr-xs"
            dropdown-icon="expand_more"
            fill-input
            :bottom-slots="false"
            dense
            outlined
            input-debounce="500"
            emit-value
            map-options
            option-label="Name"
            option-value="Id"
            ref="from_select"
            :options="storageGroups"
          />
        </div>
      </div>
    </div>
  </q-card>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import _ from "lodash";
import {
  StorageGroupWithFigures,
  StorageWithFigures,
} from "../../../../models/interfaces/storage/storage-models";
import { storageStore } from "../../../../store/storage/storage-instances";
import NativeInput from "../../../common/basic/NativeInput.vue";
import SelectorPopup from '@/components/common/aux-selector/SelectorPopup.vue';
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { CommonEntity } from "../../../../models/interfaces/common";
import { reactive } from "vue";

const pendingStorageRenameById = new Map<number, string>();

@Component({
  components: { NativeInput, SelectorPopup, AppIcon },
  emits: ["close"],
})
export default class StorageInfoComponent extends Vue {
  @Prop({ default: undefined })
  currentStorage!: StorageWithFigures | undefined;
  @Prop({ default: -1 })
  storageGroupId!: number;
  @Prop({ default: false })
  editNameOnStart!: boolean

  changesDone: boolean = false;
  storage: any = undefined;
  currentStorageGroupId: number = 0; // model

  tmp_resp = reactive([]);
  selectedExistingResponsible: CommonEntity | undefined = undefined;
  showSelector: boolean = false;
  storageNameDraft: string = "";
  lastConfirmedStorageName: string = "";

  created() {
    this.storage = storageStore();
    this.currentStorageGroupId = this.storageGroupId;
    this.syncStorageNameState(this.currentStorage?.Name || "");
  }

  mounted() {
    if (this.editNameOnStart) {
      this.focusInput();
    }
  }

  get CanWrite() {
    return this.storage.storageAccess?.CanWrite; 
  }

  handleMenuShow() {
    this.tmp_resp.splice(0);
    this.showSelector = !this.showSelector;
  }

  get tmp_resp_mut() {
    return _.cloneDeep(this.tmp_resp);
  }

  @Watch("tmp_resp_mut")
  async respChanged(newval: any, oldval: any) {
    if (!this.tmp_resp_mut.length || _.isEqual(newval, oldval)) {
      return;
    }

    console.log(this.tmp_resp_mut[this.tmp_resp_mut.length - 1])

    const tmp_resps: any =
      this.tmp_resp_mut[this.tmp_resp_mut.length - 1];
    if (tmp_resps) {
      this.changeStorageResponsible(tmp_resps.Entity);
    }
    this.tmp_resp = reactive([
      this.tmp_resp_mut[this.tmp_resp_mut.length - 1],
    ]);

    this.showSelector = false;
  }

  get storageGroups(): StorageGroupWithFigures[] {
    return this.storage.storagesWithFigures;
  }

  get storageNameFromProps(): string {
    return this.currentStorage?.Name || "";
  }

  @Watch("storageNameFromProps")
  onStorageNameFromPropsChanged(newName: string) {
    const storageId = this.currentStorage?.Id || 0;
    const normalized = this.normalizeStorageName(newName);
    const pendingName = storageId > 0 ? (pendingStorageRenameById.get(storageId) || "") : "";

    // Если сервер подтвердил ожидаемое имя — снимаем pending и синхронизируем draft.
    if (storageId > 0 && pendingName && pendingName === normalized) {
      pendingStorageRenameById.delete(storageId);
    }

    if (!pendingName || pendingName === normalized) {
      this.syncStorageNameState(normalized);
    }
  }

  onStorageNameInput(newname: string) {
    this.storageNameDraft = newname || "";
    if (!this.CanWrite || !this.currentStorage?.Id) return;
    void this.onRenameStorage(this.storageNameDraft);
  }

  normalizeStorageName(val: string): string {
    return (val || "").replace(/(\r\n|\n|\r)/g, "");
  }

  syncStorageNameState(name: string) {
    this.storageNameDraft = name || "";
    this.lastConfirmedStorageName = this.storageNameDraft;
  }

  async onRenameStorage(val: string) {
    const storageId = this.currentStorage?.Id || 0;
    if (storageId <= 0) return;

    const normalized = this.normalizeStorageName(val);
    const currentName = this.lastConfirmedStorageName || this.normalizeStorageName(this.currentStorage?.Name || "");
    const pendingName = pendingStorageRenameById.get(storageId) || "";
    if (!normalized) {
      this.storageNameDraft = this.lastConfirmedStorageName;
      return;
    }
    if (normalized === currentName || normalized === pendingName) {
      this.storageNameDraft = normalized;
      return;
    }

    pendingStorageRenameById.set(storageId, normalized);
    this.storageNameDraft = normalized;

    const response = await this.storage.renameStorage({
      Id: storageId,
      NewName: normalized,
    });

    if (response?.response_ok) {
      pendingStorageRenameById.delete(storageId);
      this.lastConfirmedStorageName = normalized;
      this.changesDone = true;
      return;
    }

    pendingStorageRenameById.delete(storageId);
    this.storageNameDraft = this.lastConfirmedStorageName;
  }

  focusInput(pos_start = false) {
    if (this.$refs.storage_name_input) {
      // @ts-ignore
      // this.inputs[level].focus();
      this.$refs.storage_name_input.startEdit();
      // if (pos_start) {
      //   this.inputs[level].nativeEl.selectionStart = 0;
      //   this.inputs[level].nativeEl.selectionEnd = 0;
      // }
    }
  }

  close() {
    if (this.changesDone) {
      // refresh in store: и для редактора складов (WithFigures), и для селекторов в movement (WithItems)
      this.storage.getStoragesWithFigures();
      this.storage.getStoragesWithItems();
    }
    this.$emit("close", null);
  }

  async changeStorageResponsible(new_resp: CommonEntity) {
    if (this.currentStorage?.Id <= 0 || !new_resp?.Id || this.currentStorage?.ResponsibleWorker?.Id === new_resp.Id) {
      return;
    }

    if (new_resp.Id >= 0) {
      const res = await this.storage.editStorageResponsibleWorker({
        StorageId: this.currentStorage?.Id,
        ResponsibleWorkerId: new_resp.Id
      });

      if (res.response_ok) {
        this.selectedExistingResponsible = _.cloneDeep(new_resp);
      }
    }
    this.changesDone = true;
  }

  async changeStorageGroup(targetGroupId: number) {
    if (this.currentStorage?.Id <= 0 || this.currentStorageGroupId === targetGroupId) {
      return;
    }

    if (targetGroupId >= 0) {
      await this.storage.addStorageToGroup(
        this.currentStorage?.Id,
        targetGroupId
      );
    } else {
      await this.storage.removeStorageFromGroup(this.currentStorage?.Id);
    }
    this.changesDone = true;
  }
}
</script>

<style lang="scss"></style>
